import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import api from "@/utils/api";

const provisionalSanctionSchema = z.object({
  status: z.string().min(1,"Please select Recommended or Not Recommended"),
  sanction_order_no: z.string().optional(),
  sanction_order_date: z.string().optional(),
  sanction_order_amount: z.string().optional(),
  description: z.string().optional(),
  sanctioned_copy: z.any().optional(),
}).superRefine((data, ctx) => { 
  if (data.status === "recommended") {
    if (!data.sanction_order_no || data.sanction_order_no.trim() === "") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Sanction Order Number is required",
        path: ["sanction_order_no"],
      });
    }
    
    if (!data.sanction_order_date || data.sanction_order_date.trim() === "") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Sanction Order Date is required",
        path: ["sanction_order_date"],
      });
    }
    
    if (!data.sanction_order_amount || data.sanction_order_amount.trim() === "") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Sanction Order Amount is required",
        path: ["sanction_order_amount"],
      });
    }
    
    if (!data.sanctioned_copy || data.sanctioned_copy.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Sanctioned Copy is required",
        path: ["sanctioned_copy"],
      });
    }
  }
});

const ProvisionalSanctionForm = ({ formType, form_id, setData }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    handleSubmit,
    register,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    resolver: zodResolver(provisionalSanctionSchema),
    defaultValues: {
      status: "",
      sanction_order_no: "",
      sanction_order_date: "",
      sanction_order_amount: "",
      description: "",
    },
  });

  const selectedStatus = watch("status");

  const onSubmit = async (formData) => {
    try {
      setIsSubmitting(true);

      const submitData = new FormData();
      submitData.append("id", form_id);
      submitData.append("status", formData.status);
 
      if (formData.status === "recommended") {
        submitData.append("sanction_order_no", formData.sanction_order_no);
        submitData.append("sanction_order_date", formData.sanction_order_date);
        submitData.append("sanction_order_amount", formData.sanction_order_amount);
        
        if (formData.sanctioned_copy?.[0]) {
          submitData.append("sanctioned_copy", formData.sanctioned_copy[0]);
        }
      }

      submitData.append("description", formData.description || "");

      const response = await api.post(
        `${formType}/provisional_sanction`,
        submitData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      setData(response?.data?.data);
      toast({
        title: "Saved",
        description: "Submitted successfully.",
        variant: "success",
      });
    } catch (error) {
      console.error("Submission error:", error);
      toast({
        title: "Submission Failed",
        description: error.response?.data?.message || "Failed to submit form",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 mt-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="status" required>
            Select Provisional Sanction Recommend or Not Recommend
          </Label>
          <Select
            value={selectedStatus}
            onValueChange={(value) => setValue("status", value)}
          >
            <SelectTrigger
              className={errors.status ? "border-red-500" : ""}
            >
              <SelectValue placeholder="Select Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recommended">Approve</SelectItem>
              <SelectItem value="not_recommended">Reject</SelectItem>
            </SelectContent>
          </Select>
          {errors.status && (
            <p className="text-sm text-red-500">
              {errors.status.message}
            </p>
          )}
        </div>

        {selectedStatus === "recommended" && (
          <>
            <div className="space-y-2">
              <Label htmlFor="sanction_order_no" required>
                Provisional Sanction Order Number
              </Label>
              <Input
                id="sanction_order_no"
                {...register("sanction_order_no")}
                placeholder="Enter sanction order number"
                className={errors.sanction_order_no ? "border-red-500" : ""}
              />
              {errors.sanction_order_no && (
                <p className="text-sm text-red-500">
                  {errors.sanction_order_no.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="sanction_order_date" required>
                Provisional Sanction Order Date
              </Label>
              <Input
                id="sanction_order_date"
                type="date"
                {...register("sanction_order_date")}
                className={errors.sanction_order_date ? "border-red-500" : ""}
              />
              {errors.sanction_order_date && (
                <p className="text-sm text-red-500">
                  {errors.sanction_order_date.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="sanction_order_amount" required>
                Provisional Sanction Order Amount
              </Label>
              <Input
                id="sanction_order_amount"
                type="text"
                {...register("sanction_order_amount")}
                placeholder="Enter amount"
                className={errors.sanction_order_amount ? "border-red-500" : ""}
              />
              {errors.sanction_order_amount && (
                <p className="text-sm text-red-500">
                  {errors.sanction_order_amount.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="sanctioned_copy" required>
                Provisional Sanctioned Copy
              </Label>
              <Input
                id="sanctioned_copy"
                type="file"
                accept=".jpg,.jpeg,.png,.pdf"
                {...register("sanctioned_copy")}
                className={errors.sanctioned_copy ? "border-red-500" : ""}
              />
              {errors.sanctioned_copy && (
                <p className="text-sm text-red-500">
                  {errors.sanctioned_copy.message}
                </p>
              )}
              <p className="text-xs text-gray-500">
                Allowed formats: JPG, PNG, PDF (Max: 2MB)
              </p>
            </div>
          </>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          rows={4}
          {...register("description")}
          placeholder="Enter description (optional)"
          className={errors.description ? "border-red-500" : ""}
        />
        {errors.description && (
          <p className="text-sm text-red-500">{errors.description.message}</p>
        )}
      </div>

      <div className="flex justify-end gap-4">
        <Button 
          type="button"
          onClick={handleSubmit(onSubmit)}
          disabled={isSubmitting} 
          className="min-w-[200px]"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Submitting...
            </>
          ) : (
            "Submit"
          )}
        </Button>
      </div>
    </div>
  );
};

export default ProvisionalSanctionForm;